import { DisplayMode, PostMessageErrorType } from '@flywire/internal-components/model';
import {
  InjectPaymentElementAppearanceSettings,
  MESSAGES
} from '@flywire/internal-components/services/iframeMessageBroker/model';

import IframeMessageBroker from '../../../../../internal-components/src/services/iframeMessageBroker/iframeMessageBroker';
import {
  InternalElementFactoryOptions,
  PaymentElementPublicOptions,
  SdkElement
} from '../../../model';
import { EventBus, EventBusType } from '../../EventBus';
import { createIframe } from '../createIframe';
import { checkoutUrlBuilder } from './checkoutUrlBuilder';

declare const FLYWIREJS_DEBUG: boolean;

export type PaymentElementOptions = InternalElementFactoryOptions & PaymentElementPublicOptions;

export const PaymentElement = (options: PaymentElementOptions): SdkElement => {
  const { events = [], displayMode = 'full-screen' } = options;
  const eventBus = EventBus(events);
  eventBus.onEvent('init', []);

  const mount = (id?: string) => {
    eventBus.onEvent('mount', []);
    const url = checkoutUrlBuilder(options);
    const { iframe, container } = createIframe({ containerId: id, src: url, displayMode });

    sendAppearanceValuesToPaymentElementIframe(
      IframeMessageBroker.getInstance(),
      iframe,
      options.appearance,
      eventBus
    );

    setupMessageHandlers({ container, eventBus, displayMode });
  };

  return {
    mount,
    onEvent: (key, fn) => eventBus.addEvent(key, fn),
    registeredEvents: eventBus.registeredEvents
  };
};

const sendAppearanceValuesToPaymentElementIframe = (
  broker: ReturnType<(typeof IframeMessageBroker)['getInstance']>,
  iframe: HTMLIFrameElement,
  appearance: InternalElementFactoryOptions['appearance'],
  eventBus: EventBusType
) => {
  broker.addListener(MESSAGES.PAYMENT_ELEMENT_LOADED, () => {
    eventBus.onEvent('loaded', {});
    if (appearance) {
      broker.sendMessage<InjectPaymentElementAppearanceSettings>(
        MESSAGES.INJECT_PAYMENT_ELEMENT_APPEARANCE_SETTINGS,
        {
          appearance: { ...appearance }
        },
        iframe
      );
    }
  });
};

const setupMessageHandlers = ({
  eventBus,
  container,
  displayMode
}: {
  eventBus: EventBusType;
  container: HTMLElement;
  displayMode: DisplayMode;
}) => {
  let handler: ((event: MessageEvent) => void) | null = null;

  const cleanup = () => {
    if (handler) {
      window.removeEventListener('message', handler);
      handler = null;
    }
  };

  const onSuccessCallback = (data: unknown) => {
    cleanup();
    eventBus.onEvent('success', data);

    if (displayMode === 'full-screen') {
      container.remove();
    }
  };

  const onErrorCallback = (data: PostMessageErrorType) => {
    cleanup();
    eventBus.onEvent('error', data);

    if (displayMode === 'full-screen') {
      container.remove();
    }
  };

  handler = addMessageHandler(onSuccessCallback, onErrorCallback);
};

const addMessageHandler = (
  onSuccess: (data: unknown) => void,
  onError: (data: PostMessageErrorType) => void
) => {
  const handler = (event: MessageEvent) => {
    if (/\.flywire\.(com|lol|cc)/.test(event.origin) || FLYWIREJS_DEBUG) {
      const result = event.data;
      if (result.source !== 'checkout_session') {
        return;
      }

      if (result.success && result.confirm_url) {
        onSuccess(result);
      } else {
        onError(result);
      }
    }
  };
  window.addEventListener('message', handler);

  return handler;
};
